import React, { useEffect, useState } from "react";
import {
    Box, Grid, Typography, Select, MenuItem, Checkbox,
    Button, IconButton, TextField,
    FormControl, InputLabel, Chip, OutlinedInput, ListItemText,
    Table, TableHead, TableBody, TableRow, TableCell, TableContainer, Paper,
    Dialog, DialogTitle, DialogContent, DialogActions, Collapse
} from "@mui/material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import FilterListIcon from "@mui/icons-material/FilterList";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import InfoIcon from "@mui/icons-material/Info";
import api from "../axios/axiosInstance";

import useFilteredStudents from "../hooks/useFilteredStudents";
import useSubjectsAndUnits from "../hooks/useSubjectsAndUnits";
import BatchCommentLimitDialog from "../dialogs/BatchCommentLimitDialog";
import handleAIError from "../utils/handleAIError";
import { FloatingGradelyLoader } from "../loadingIcons/LoadingIcons";

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
    PaperProps: {
        style: {
            maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
            width: 250,
        },
    },
};

const BatchSmartSubjectComment = ({ setActiveTabGlobal }) => {
    // Filters
    const [classLevel, setClassLevel] = useState("");
    const [sectionLevel, setSectionLevel] = useState("");
    const [academicYear, setAcademicYear] = useState("");
    const [filtersExpanded, setFiltersExpanded] = useState(false);

    // Selections
    const [selectedStudents, setSelectedStudents] = useState([]);
    const [selectedSubject, setSelectedSubject] = useState("");
    const [selectedUnits, setSelectedUnits] = useState([]); // GLOBAL units only (ids)
    const [termNumber, setTermNumber] = useState("");
    const [minWords, setMinWords] = useState(350);
    const [maxWords, setMaxWords] = useState(400);
    const [minWordsError, setMinWordsError] = useState("");
    const [maxWordsError, setMaxWordsError] = useState("");
    const [customPrompt, setCustomPrompt] = useState("");

    // UI
    const [isLoading, setIsLoading] = useState(false);
    const [successDialog, setSuccessDialog] = useState({ open: false, message: "", title: "" });
    const [errorDialog, setErrorDialog] = useState({ open: false, message: "" });
    const [showLimitDialog, setShowLimitDialog] = useState(false);
    const [unitsModal, setUnitsModal] = useState({ open: false, studentName: "", units: [] });

    // DOK Reports retrieval
    const [dokReportsRetrieved, setDokReportsRetrieved] = useState(false);
    const [retrieveButtonDisabled, setRetrieveButtonDisabled] = useState(false);
    const [cooldownTimer, setCooldownTimer] = useState(0);

    // Results
    const [batchResults, setBatchResults] = useState({});
    const [processedCount, setProcessedCount] = useState(0);
    const [errors, setErrors] = useState({});

    // Per-student table data
    // { [studentId]: { overallAssessment } }
    const [studentTableData, setStudentTableData] = useState({});
    const [availableUnits, setAvailableUnits] = useState([]); // [{id, unit_name, dok_exists, ...}]
    const [existingSummaries, setExistingSummaries] = useState({});

    // Hooks
    const filteredStudents = useFilteredStudents(classLevel, sectionLevel, academicYear);
    const { subjects } = useSubjectsAndUnits(selectedSubject);

    // Helpers to list class/section options
    const getUniqueClassLevels = () => {
        const levels = [...new Set(filteredStudents.map(s => s.class_level))].filter(Boolean);
        return levels.sort();
    };
    const getUniqueSections = () => {
        const sections = [...new Set(filteredStudents.map(s => s.section_level))].filter(Boolean);
        return sections.sort();
    };

    // Reset when filters change
    useEffect(() => {
        setSelectedStudents([]);
        setStudentTableData({});
        setExistingSummaries({});
        setBatchResults({});
        setDokReportsRetrieved(false);
    }, [classLevel, sectionLevel, academicYear]);

    // Fetch units when subject changes
    useEffect(() => {
        if (selectedSubject) {
            fetchAvailableUnits();
        } else {
            setAvailableUnits([]);
            setSelectedUnits([]);
        }
        // Reset DOK reports when subject changes
        setDokReportsRetrieved(false);
        setStudentTableData({});
        setExistingSummaries({});
    }, [selectedSubject]);

    // Reset DOK reports when students, units, or term changes
    useEffect(() => {
        setDokReportsRetrieved(false);
        setStudentTableData({});
        setExistingSummaries({});
        setBatchResults({});
    }, [selectedStudents, selectedUnits, termNumber]);

    // Cooldown timer effect
    useEffect(() => {
        let interval;
        if (cooldownTimer > 0) {
            interval = setInterval(() => {
                setCooldownTimer(prev => {
                    if (prev <= 1) {
                        setRetrieveButtonDisabled(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => {
            if (interval) clearInterval(interval);
        };
    }, [cooldownTimer]);

    const fetchAvailableUnits = async () => {
        if (!selectedSubject) return;
        try {
            const firstStudent = filteredStudents[0] || { academic_year: new Date().getFullYear(), id: 1 };
            const unitsRes = await api.get(
                `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/check-dok-report/`,
                {
                    params: {
                        student: firstStudent.id,
                        subject: selectedSubject,
                        academic_year: firstStudent.academic_year,
                        term_number: 1
                    },
                    withCredentials: true
                }
            );
            const units = unitsRes.data.units || [];
            setAvailableUnits(units);
            const validUnits = units.filter(u => u.dok_exists).map(u => u.id);
            setSelectedUnits(validUnits);
        } catch (error) {
            console.error("Error fetching units:", error);
            setAvailableUnits([]);
            setSelectedUnits([]);
        }
    };

    const handleRetrieveDOKReports = async () => {
        if (selectedStudents.length === 0 || !selectedSubject || selectedUnits.length === 0 || !termNumber) {
            setErrorDialog({ open: true, message: "Please select students, subject, units, and term before retrieving DOK reports." });
            return;
        }

        setIsLoading(true);
        setRetrieveButtonDisabled(true);

        try {
            const response = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/batch-retrieve-dok-reports/`,
                {
                    students: selectedStudents.map(id => parseInt(id, 10)),
                    subject: parseInt(selectedSubject, 10),
                    units: selectedUnits,
                    term_number: parseInt(termNumber, 10)
                },
                { withCredentials: true }
            );

            const data = response.data;

            // Process the response and populate table data
            const newTableData = {};
            const newExistingSummaries = {};

            selectedStudents.forEach(studentId => {
                const studentData = data.students[studentId];
                if (studentData) {
                    newTableData[studentId] = {
                        overallAssessment: studentData.overall_assessment || "Proficient",
                        dokData: studentData // Store the full DOK data for later use
                    };
                    newExistingSummaries[studentId] = studentData.existing_summary || "";
                }
            });

            setStudentTableData(newTableData);
            setExistingSummaries(newExistingSummaries);
            setDokReportsRetrieved(true);
            setErrorDialog({ open: false, message: "" });
            setSuccessDialog({
                open: true,
                title: "DOK Reports Retrieved!",
                message: `Successfully retrieved DOK reports for ${selectedStudents.length} students!`
            });

            // Start 30-second cooldown
            setCooldownTimer(30);

        } catch (error) {
            console.error("Error retrieving DOK reports:", error);
            setErrorDialog({ open: true, message: error?.response?.data?.error || "Failed to retrieve DOK reports. Please try again." });
            setRetrieveButtonDisabled(false);
        } finally {
            setIsLoading(false);
        }
    };

    const prepareStudentUnitsFromDOKData = (studentId) => {
        const studentData = studentTableData[studentId]?.dokData;
        if (!studentData || !studentData.units) return [];

        const units = [];
        Object.values(studentData.units).forEach(unit => {
            if (!unit.dok_exists || !unit.achieved_skills || Object.keys(unit.achieved_skills).length === 0) {
                return; // Skip units without DOK data or achieved skills
            }

            const entries = [];
            Object.entries(unit.achieved_skills).forEach(([dokLevel, skills]) => {
                skills.forEach(skill => {
                    entries.push({
                        dok_level_id: skill.dok_level_id,
                        skill_description: skill.skill_description,
                        is_achieved: true,
                        dok_level_label: skill.dok_level_label
                    });
                });
            });

            if (entries.length > 0) {
                units.push({
                    unit_id: unit.unit_id,
                    unit_name: unit.unit_name,
                    // May be blank if no overall level was picked for this unit yet — leave it
                    // blank rather than fabricating a level; the comment's calibration comes
                    // from the student's overall_assessment, not from each unit's own level.
                    overall_unit_assessment: unit.overall_unit_assessment || "",
                    entries: entries
                });
            }
        });

        return units;
    };

    const handleStudentSelection = (event) => {
        const value = event.target.value;
        const newSelection = typeof value === 'string' ? value.split(',') : value;
        if (newSelection.length <= 10) {
            setSelectedStudents(newSelection);
        }
    };

    const handleGenerate = async () => {
        if (selectedStudents.length === 0) {
            setErrorDialog({ open: true, message: "Please select at least one student." });
            return;
        }
        if (!selectedSubject || selectedUnits.length === 0 || !termNumber) {
            setErrorDialog({ open: true, message: "Please select subject, units, and term." });
            return;
        }
        if (!dokReportsRetrieved) {
            setErrorDialog({ open: true, message: "Please retrieve DOK reports first by clicking 'Retrieve Student DOK Reports' button." });
            return;
        }

        setIsLoading(true);
        const startTime = Date.now();

        try {
            // Build the payload using pre-fetched DOK data
            const studentsPayload = [];

            for (const sid of selectedStudents) {
                const row = studentTableData[sid] || {};

                // Use pre-fetched DOK data
                const unitsForStudent = prepareStudentUnitsFromDOKData(sid);

                // Only include students that have at least one unit with achieved entries
                if (!unitsForStudent || unitsForStudent.length === 0) {
                    continue;
                }

                studentsPayload.push({
                    student: parseInt(sid, 10),
                    overall_assessment: row.overallAssessment || "Proficient",
                    units: unitsForStudent
                });
            }

            if (studentsPayload.length === 0) {
                setErrorDialog({ open: true, message: "No students have valid DOK data with achieved skills. Please check the retrieved DOK reports." });
                return;
            }

            const body = {
                subject_id: parseInt(selectedSubject, 10),
                term_number: parseInt(termNumber, 10),
                min_words: minWords,
                max_words: maxWords,
                custom_prompt: customPrompt.trim(),
                students: studentsPayload
            };

            const res = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/progress-card/batch-generate-student-summary/`,
                body,
                { withCredentials: true }
            );

            const endTime = Date.now();
            const duration = ((endTime - startTime) / 1000).toFixed(2);
            console.log(`✅ Batch AI summaries generated in ${duration} seconds`);

            setBatchResults(res.data.summaries || {});
            setProcessedCount(res.data.processed_count || 0);
            setErrors(res.data.errors || {});
            setErrorDialog({ open: false, message: "" });

            // Check for errors and show them in error dialog if any
            const errorEntries = Object.entries(res.data.errors || {});
            if (errorEntries.length > 0) {
                const errorMessages = errorEntries.map(([studentId, error]) =>
                    `Student ID ${studentId}: ${error}`
                ).join('\n');
                setErrorDialog({
                    open: true,
                    message: `Some students could not be processed:\n\n${errorMessages}`
                });
            } else {
                setSuccessDialog({
                    open: true,
                    title: "Comments Generated Successfully!",
                    message: `Generated ${res.data.processed_count || 0} summaries successfully!`
                });
            }
        } catch (err) {
            handleAIError(err, {
                setErrorMsg: (msg) => {
                    if (msg && msg.trim()) {
                        setErrorDialog({ open: true, message: msg });
                    } else {
                        setErrorDialog({ open: false, message: "" });
                    }
                },
                setShowLimitDialog
            });
        } finally {
            setIsLoading(false);
        }
    };

    const handleCopyToClipboard = (summary) => {
        navigator.clipboard.writeText(summary || "");
        setSuccessDialog({
            open: true,
            title: "Copied to Clipboard!",
            message: "Summary has been copied to your clipboard."
        });
    };

    const handleOverallAssessmentChange = (studentId, value) => {
        setStudentTableData(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                overallAssessment: value
            }
        }));
    };


    const handleSummaryEdit = (studentId, summary) => {
        setBatchResults(prev => ({
            ...prev,
            [studentId]: {
                ...prev[studentId],
                summary: summary
            }
        }));
    };

    const handleUnitsModalOpen = (studentId) => {
        const student = filteredStudents.find(s => s.id.toString() === studentId);
        const row = studentTableData[studentId] || {};

        if (row.dokData && row.dokData.units) {
            const units = Object.values(row.dokData.units);
            setUnitsModal({
                open: true,
                studentName: `${student?.first_name} ${student?.last_name}`,
                units: units
            });
        }
    };

    const handleSaveSummaries = async () => {
        if (Object.keys(batchResults).length === 0) {
            setErrorDialog({ open: true, message: "No summaries to save." });
            return;
        }

        setIsLoading(true);
        try {
            // Prepare batch data for the new endpoint
            const summaries = Object.entries(batchResults).map(([studentId, result]) => {
                const student = filteredStudents.find(s => s.id.toString() === studentId);
                if (!student) return null;

                const row = studentTableData[studentId] || {};
                return {
                    student: parseInt(studentId, 10),
                    subject: parseInt(selectedSubject, 10),
                    academic_year: student.academic_year,
                    summary: result.summary,
                    overall_assessment: row.overallAssessment || "Proficient",
                    term_number: parseInt(termNumber, 10),
                };
            }).filter(Boolean);

            // Single API call to batch save endpoint
            const response = await api.post(
                `${process.env.REACT_APP_BACKEND_URL}/grades/batch-save-student-summaries/`,
                { summaries },
                { withCredentials: true }
            );

            // Extract count from message like "5 summaries saved/updated."
            const count = summaries.length;
            setSuccessDialog({
                open: true,
                title: "Summaries Saved Successfully!",
                message: `${count} summaries have been saved to the database.`
            });
            setErrorDialog({ open: false, message: "" });

            // Log any errors from individual saves
            if (response.data.errors && Object.keys(response.data.errors).length > 0) {
                console.warn("Some summaries had errors:", response.data.errors);
            }
        } catch (error) {
            setErrorDialog({ open: true, message: "Failed to save summaries. Please try again." });
            console.error("Batch save error:", error);
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <Box
            p={{ xs: 2, sm: 3 }}
            sx={{
                width: '100%',
                maxWidth: '100vw',
                overflowX: 'hidden'
            }}
        >
            {/* Loading overlay */}
            {isLoading && (
                <Box
                    sx={{
                        position: "fixed",
                        inset: 0,
                        backgroundColor: "rgba(255,255,255,0.9)",
                        backdropFilter: "blur(8px)",
                        zIndex: 2000,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                    }}
                >
                    <Box
                        sx={{
                            p: { xs: 2, sm: 3, md: 4 },
                            borderRadius: 4,
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            backgroundColor: "white",
                            maxWidth: { xs: 320, sm: 400 },
                            mx: { xs: 1, sm: 2 },
                            boxShadow: 8,
                            textAlign: 'center'
                        }}
                    >
                        <FloatingGradelyLoader
                            size={{ xs: 50, sm: 60 }}
                            showText={true}
                            text={`Generating comments for ${selectedStudents.length} students. Please wait 1-2 minutes while content is loading...`}
                        />
                    </Box>
                </Box>
            )}


            <Typography
                variant="h5"
                fontWeight="bold"
                mb={{ xs: 2, md: 3 }}
                sx={{
                    fontSize: { xs: '0.95rem', sm: '1rem' },
                    textAlign: { xs: 'center', sm: 'left' }
                }}
            >
                Generate comments for multiple students (10 max) - Select ALL fields
            </Typography>

            <Grid container spacing={{ xs: 2, md: 3 }}>
                {/* 1. Collapsible Filters */}
                <Grid item xs={12} lg={4}>
                    <Paper
                        elevation={2}
                        sx={{
                            mb: 3,
                            borderRadius: 2,
                            border: filtersExpanded ? '2px solid #4caf50' : '1px solid #e0e0e0',
                            transition: 'all 0.3s ease'
                        }}
                    >
                        <Box
                            onClick={() => setFiltersExpanded(!filtersExpanded)}
                            sx={{
                                p: 2,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'space-between',
                                cursor: 'pointer',
                                backgroundColor: filtersExpanded ? '#f1f8e9' : 'transparent',
                                borderRadius: filtersExpanded ? '8px 8px 0 0' : '8px',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                    backgroundColor: '#f5f5f5'
                                }
                            }}
                        >
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <FilterListIcon
                                    sx={{
                                        color: filtersExpanded ? '#4caf50' : '#666',
                                        fontSize: 20
                                    }}
                                />
                                <Typography
                                    fontWeight="bold"
                                    sx={{
                                        color: filtersExpanded ? '#4caf50' : 'text.primary',
                                        fontSize: '0.95rem'
                                    }}
                                >
                                    Filter Students
                                </Typography>
                                {(classLevel || sectionLevel || academicYear) && (
                                    <Box
                                        sx={{
                                            width: 8,
                                            height: 8,
                                            borderRadius: '50%',
                                            backgroundColor: '#ff5722',
                                            ml: 1
                                        }}
                                    />
                                )}
                            </Box>
                            {filtersExpanded ?
                                <ExpandLessIcon sx={{ color: '#4caf50' }} /> :
                                <ExpandMoreIcon sx={{ color: '#666' }} />
                            }
                        </Box>

                        <Collapse in={filtersExpanded}>
                            <Box sx={{ p: 2, pt: 0 }}>
                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Class Level
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={classLevel}
                                        onChange={(e) => setClassLevel(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Classes</MenuItem>
                                        {getUniqueClassLevels().map(level => (
                                            <MenuItem key={level} value={level}>{level}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Section
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={sectionLevel}
                                        onChange={(e) => setSectionLevel(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Sections</MenuItem>
                                        {getUniqueSections().map(section => (
                                            <MenuItem key={section} value={section}>{section}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                    <Typography
                                        fontWeight="600"
                                        sx={{ mb: 1, fontSize: '0.875rem', color: '#333' }}
                                    >
                                        Academic Year
                                    </Typography>
                                    <Select
                                        fullWidth
                                        size="small"
                                        value={academicYear}
                                        onChange={(e) => setAcademicYear(e.target.value)}
                                        displayEmpty
                                        sx={{
                                            '& .MuiOutlinedInput-root': {
                                                borderRadius: '8px',
                                                backgroundColor: 'white'
                                            }
                                        }}
                                    >
                                        <MenuItem value="">All Years</MenuItem>
                                        {[
                                            "2025-2026",
                                            "2026-2027",
                                            "2027-2028",
                                            "2028-2029",
                                            "2029-2030",
                                        ].map((year) => (
                                            <MenuItem key={year} value={year}>{year}</MenuItem>
                                        ))}
                                    </Select>
                                </Box>

                                {/* Clear Filters Button */}
                                {(classLevel || sectionLevel || academicYear) && (
                                    <Button
                                        variant="outlined"
                                        size="small"
                                        fullWidth
                                        onClick={() => {
                                            setClassLevel("");
                                            setSectionLevel("");
                                            setAcademicYear("");
                                        }}
                                        sx={{
                                            mt: 1,
                                            textTransform: 'none',
                                            borderRadius: '8px'
                                        }}
                                    >
                                        Clear All Filters
                                    </Button>
                                )}

                                <Typography variant="body2" color="textSecondary" mt={2} sx={{ fontSize: '.9rem', textAlign: 'center' }}>
                                    {filteredStudents.length} students available
                                </Typography>
                            </Box>
                        </Collapse>
                    </Paper>
                </Grid>

                {/* 2. Select Students + 3. Configuration */}
                <Grid item xs={12} lg={8}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        mb={2}
                        sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}
                    >
                        Select Students
                    </Typography>

                    <FormControl fullWidth>
                        <InputLabel>Select Students (Max 10)</InputLabel>
                        <Select
                            multiple
                            value={selectedStudents}
                            onChange={handleStudentSelection}
                            input={<OutlinedInput label="Select Students (Max 10)" />}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected
                                        .map(id => {
                                            const s = filteredStudents.find(x => x.id === parseInt(id, 10));
                                            return s ? `${s.first_name} ${s.last_name}` : "";
                                        })
                                        .filter(Boolean)
                                        .map((name, idx) => <Chip key={idx} label={name} size="small" />)}
                                </Box>
                            )}
                            MenuProps={MenuProps}
                        >
                            {filteredStudents.map((student) => {
                                const val = student.id.toString();
                                const disabled = selectedStudents.length >= 10 && !selectedStudents.includes(val);
                                return (
                                    <MenuItem key={val} value={val} disabled={disabled}>
                                        <Checkbox checked={selectedStudents.includes(val)} />
                                        {student.first_name} {student.last_name} ({student.class_level} - {student.section_level})
                                    </MenuItem>
                                );
                            })}
                        </Select>
                    </FormControl>

                    <Typography variant="body2" color="textSecondary" mt={1} sx={{ fontSize: '.9rem' }}>
                        {selectedStudents.length}/10 students selected
                    </Typography>

                    {/* 3. Configuration */}
                    <Box mt={3}>


                        <Grid container spacing={{ xs: 2, md: 2 }}>
                            <Grid item xs={12} md={4}>
                                <Typography fontWeight="bold" sx={{ mb: 1 }}>Subject</Typography>
                                <Select fullWidth size="small" value={selectedSubject} onChange={e => setSelectedSubject(e.target.value)}>
                                    <MenuItem value="">Select Subject</MenuItem>
                                    {subjects.map(sub => (
                                        <MenuItem key={sub.id} value={sub.id}>{`${sub.subject_name} ${sub.class_level}`}</MenuItem>
                                    ))}
                                </Select>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography fontWeight="bold" sx={{ mb: 1 }}>Units</Typography>
                                <FormControl fullWidth size="small">
                                    <Select
                                        multiple
                                        value={selectedUnits}
                                        onChange={(e) => setSelectedUnits(e.target.value)}
                                        input={<OutlinedInput />}
                                        renderValue={(selected) => (
                                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                                {selected.map((unitId) => {
                                                    const unit = availableUnits.find(u => u.id === unitId);
                                                    return (
                                                        <Chip key={unitId} label={unit?.unit_name || `Unit ${unitId}`} size="small" />
                                                    );
                                                })}
                                            </Box>
                                        )}
                                        MenuProps={MenuProps}
                                    >
                                        {availableUnits.map(unit => (
                                            <MenuItem key={unit.id} value={unit.id}>
                                                <Checkbox checked={selectedUnits.includes(unit.id)} />
                                                <ListItemText primary={unit.unit_name} />
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>

                            <Grid item xs={12} md={4}>
                                <Typography fontWeight="bold" sx={{ mb: 1 }}>Term</Typography>
                                <Select fullWidth size="small" value={termNumber} onChange={e => setTermNumber(e.target.value)}>
                                    <MenuItem value="">Select Term</MenuItem>
                                    <MenuItem value={1}>Term 1</MenuItem>
                                    <MenuItem value={2}>Term 2</MenuItem>
                                    <MenuItem value={3}>Term 3</MenuItem>
                                </Select>
                            </Grid>
                        </Grid>

                        {/* Retrieve DOK Reports Button */}
                        <Box mt={3}>
                            <Button
                                variant="contained"
                                color="primary"
                                size="large"
                                onClick={handleRetrieveDOKReports}
                                disabled={
                                    selectedStudents.length === 0 ||
                                    !selectedSubject ||
                                    selectedUnits.length === 0 ||
                                    !termNumber ||
                                    retrieveButtonDisabled
                                }
                                sx={{
                                    minWidth: { xs: 240, sm: 280 },
                                    width: { xs: '100%', sm: 'auto' },
                                    background: dokReportsRetrieved ?
                                        'linear-gradient(45deg, #4CAF50 30%, #66BB6A 90%)' :
                                        undefined
                                }}
                            >
                                {cooldownTimer > 0 ? (
                                    `Retrieve Available in ${cooldownTimer}s`
                                ) : dokReportsRetrieved ? (
                                    "✅ DOK Assessments Retrieved"
                                ) : (
                                    `Retrieve DOK assessments (${selectedStudents.length} students)`
                                )}
                            </Button>

                            {dokReportsRetrieved && (
                                <Chip
                                    icon={<CheckCircleIcon />}
                                    label="DOK assessments retrieved successfully! You may now proceed to generate comments for the selected students."
                                    variant="outlined"
                                    sx={{
                                        mt: 1,
                                        backgroundColor: '#f1f8e9',
                                        borderColor: '#4caf50',
                                        color: '#2e7d32',
                                        '& .MuiChip-icon': { color: '#4caf50' },
                                        fontSize: '0.75rem',
                                        height: 'auto',
                                        '& .MuiChip-label': { whiteSpace: 'normal', padding: '8px' }
                                    }}
                                />
                            )}

                            {selectedStudents.length > 0 && selectedSubject && selectedUnits.length > 0 && termNumber && !dokReportsRetrieved && (
                                <Box
                                    sx={{
                                        mt: 2,
                                        p: 2,
                                        backgroundColor: '#e3f2fd',
                                        borderRadius: 2,
                                        border: '1px solid #bbdefb',
                                        borderLeft: '4px solid #2196f3'
                                    }}
                                >
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            color: '#1565c0',
                                            fontWeight: 500,
                                            fontSize: '0.875rem',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 1
                                        }}
                                    >
                                        💡 Click the button above to retrieve DOK assessments before generating comments.
                                    </Typography>
                                </Box>
                            )}
                        </Box>
                    </Box>
                </Grid>
            </Grid>

            {/* 4. Per-student table - only show after DOK reports retrieved */}
            {selectedStudents.length > 0 && dokReportsRetrieved && (
                <Box mt={4}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        mb={2}
                        sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}
                    >
                        4. Generate Comment
                    </Typography>

                    <Box
                        display="flex"
                        gap={2}
                        mb={2}
                        flexWrap="wrap"
                        sx={{
                            flexDirection: { xs: 'column', sm: 'row' },
                            '& > button': {
                                width: { xs: '100%', sm: 'auto' },
                                minWidth: { sm: 200 }
                            }
                        }}
                    >
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleGenerate}
                            disabled={
                                selectedStudents.length === 0 ||
                                !selectedSubject ||
                                !termNumber ||
                                selectedUnits.length === 0 ||
                                !dokReportsRetrieved ||
                                !!minWordsError ||
                                !!maxWordsError
                            }
                        >
                            Generate Comments ({selectedStudents.length} students)
                        </Button>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleSaveSummaries}
                            disabled={Object.keys(batchResults).length === 0}
                        >
                            Save Comments
                        </Button>
                    </Box>

                    {/* Word Limit Controls */}
                    <Box mb={2}>
                        <Grid container spacing={2} alignItems="flex-end">
                            <Grid item xs={12} sm={6} md={3}>
                                <Typography fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>Minimum Words</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    value={minWords || ''}
                                    error={!!minWordsError}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        // Handle empty input
                                        if (inputValue === '') {
                                            setMinWords('');
                                            setMinWordsError("Please enter a number");
                                            return;
                                        }

                                        const value = parseInt(inputValue, 10);
                                        setMinWords(value);

                                        // Validate
                                        if (isNaN(value)) {
                                            setMinWordsError("Please enter a valid number");
                                        } else if (value < 100) {
                                            setMinWordsError("Minimum must be at least 100");
                                        } else if (value > 1000) {
                                            setMinWordsError("Minimum cannot exceed 1000");
                                        } else if (!isNaN(maxWords) && value >= maxWords) {
                                            setMinWordsError("Minimum must be less than maximum");
                                        } else {
                                            setMinWordsError("");
                                            // Also clear max error if it was about min >= max
                                            if (maxWordsError.includes("greater than minimum")) {
                                                setMaxWordsError("");
                                            }
                                        }
                                    }}
                                    inputProps={{ step: 50 }}
                                    helperText={minWordsError || "Min: 100, Max: 1000"}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={3}>
                                <Typography fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>Maximum Words</Typography>
                                <TextField
                                    fullWidth
                                    size="small"
                                    type="number"
                                    value={maxWords || ''}
                                    error={!!maxWordsError}
                                    onChange={(e) => {
                                        const inputValue = e.target.value;

                                        // Handle empty input
                                        if (inputValue === '') {
                                            setMaxWords('');
                                            setMaxWordsError("Please enter a number");
                                            return;
                                        }

                                        const value = parseInt(inputValue, 10);
                                        setMaxWords(value);

                                        // Validate
                                        if (isNaN(value)) {
                                            setMaxWordsError("Please enter a valid number");
                                        } else if (value < 100) {
                                            setMaxWordsError("Maximum must be at least 100");
                                        } else if (value > 1000) {
                                            setMaxWordsError("Maximum cannot exceed 1000");
                                        } else if (!isNaN(minWords) && value <= minWords) {
                                            setMaxWordsError("Maximum must be greater than minimum");
                                        } else {
                                            setMaxWordsError("");
                                            // Also clear min error if it was about min >= max
                                            if (minWordsError.includes("less than maximum")) {
                                                setMinWordsError("");
                                            }
                                        }
                                    }}
                                    inputProps={{ step: 50 }}
                                    helperText={maxWordsError || "Min: 100, Max: 1000"}
                                />
                            </Grid>

                            <Grid item xs={12} md={6}>
                                {!minWordsError && !maxWordsError ? (
                                    <Chip
                                        icon={<InfoIcon />}
                                        label={`AI will generate comments between ${minWords} and ${maxWords} words. Adjust these values to control comment length. Default: 350-400 words.`}
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: '#e3f2fd',
                                            borderColor: '#2196f3',
                                            color: '#1565c0',
                                            '& .MuiChip-icon': { color: '#2196f3' },
                                            fontSize: '0.75rem',
                                            height: 'auto',
                                            '& .MuiChip-label': { whiteSpace: 'normal', padding: '8px' }
                                        }}
                                    />
                                ) : (
                                    <Chip
                                        icon={<ErrorOutlineIcon />}
                                        label="Please fix the word limit errors above before generating comments."
                                        variant="outlined"
                                        sx={{
                                            backgroundColor: '#ffebee',
                                            borderColor: '#f44336',
                                            color: '#c62828',
                                            '& .MuiChip-icon': { color: '#f44336' },
                                            fontSize: '0.75rem',
                                            height: 'auto',
                                            '& .MuiChip-label': { whiteSpace: 'normal', padding: '8px' }
                                        }}
                                    />
                                )}
                            </Grid>

                            {/* Additional Instructions Field */}
                            <Grid item xs={12}>
                                <Typography fontWeight="bold" sx={{ mb: 1, fontSize: '0.875rem' }}>
                                    Additional Instructions (Optional)
                                </Typography>
                                <TextField
                                    fullWidth
                                    multiline
                                    minRows={3}
                                    maxRows={8}
                                    size="small"
                                    value={customPrompt}
                                    onChange={(e) => setCustomPrompt(e.target.value)}
                                    placeholder="Add any specific instructions for the AI (e.g., 'Focus on growth mindset language', 'Emphasize collaboration skills')"
                                    helperText={`${customPrompt.length}/600 characters. These instructions will apply to all student comments in this batch.`}
                                    inputProps={{ maxLength: 600 }}
                                    sx={{
                                        '& .MuiInputBase-root': {
                                            backgroundColor: 'white',
                                            fontSize: '0.875rem'
                                        }
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>

                    <Chip
                        icon={<InfoIcon />}
                        label="Only units with ✅ (assessed) will be included in comment generation. Units marked with ❌ need to be assessed first. Go to 'Assess Single Student' tab to complete missing assessments. Student must be given an overall assessment before generating comment."
                        variant="outlined"
                        sx={{
                            mb: 2,
                            backgroundColor: '#f8f9f5',
                            borderColor: '#57643F',
                            color: '#57643F',
                            '& .MuiChip-icon': { color: '#57643F' },
                            fontSize: '0.75rem',
                            height: 'auto',
                            '& .MuiChip-label': { whiteSpace: 'normal', padding: '8px' }
                        }}
                    />

                    <TableContainer
                        component={Paper}
                        elevation={2}
                        sx={{
                            overflowX: 'auto',
                            width: '100%',
                            '& .MuiTable-root': {
                                minWidth: { xs: 800, md: '100%' },
                                tableLayout: { md: 'fixed' }
                            }
                        }}
                    >
                        <Table stickyHeader>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', minWidth: 180, fontSize: '0.9rem' }}>Student</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', minWidth: 200, fontSize: '0.9rem' }}>Units Assessed</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', minWidth: 140, fontSize: '0.9rem' }}>Overall Assessment</TableCell>
                                    <TableCell
                                        sx={{
                                            fontWeight: 'bold',
                                            width: { xs: 'auto', md: '55%' },
                                            minWidth: '350px',
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        Comment
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {selectedStudents.map(studentId => {
                                    const student = filteredStudents.find(s => s.id.toString() === studentId);
                                    const row = studentTableData[studentId] || { overallAssessment: "Proficient" };

                                    const generatedSummary = batchResults[studentId]?.summary || "";
                                    const existingSummary = existingSummaries[studentId] || "";
                                    const currentSummary = generatedSummary || existingSummary || "";

                                    return (
                                        <TableRow key={studentId}>
                                            <TableCell sx={{ width: '180px', minWidth: '180px' }}>
                                                <Typography fontWeight="bold" sx={{ fontSize: '0.85rem' }}>
                                                    {student?.first_name} {student?.last_name}
                                                </Typography>
                                                <Typography variant="caption" color="textSecondary" sx={{ fontSize: '0.7rem' }}>
                                                    {student?.class_level} - {student?.section_level}
                                                </Typography>
                                            </TableCell>

                                            <TableCell sx={{ width: '200px', minWidth: '200px' }}>
                                                {row.dokData ? (
                                                    <Box
                                                        onClick={() => handleUnitsModalOpen(studentId)}
                                                        sx={{
                                                            cursor: 'pointer',
                                                            p: 1,
                                                            borderRadius: 1,
                                                            transition: 'background-color 0.2s',
                                                            '&:hover': {
                                                                backgroundColor: '#f5f5f5'
                                                            }
                                                        }}
                                                    >
                                                        <Typography variant="body2" sx={{ fontSize: '0.8rem', fontWeight: 'medium' }}>
                                                            {Object.keys(row.dokData.units).length} units
                                                        </Typography>
                                                        <Typography variant="caption" color="primary" sx={{ fontSize: '0.7rem' }}>
                                                            (Click for more info)
                                                        </Typography>
                                                    </Box>
                                                ) : (
                                                    <Typography variant="body2" color="textSecondary" sx={{ fontSize: '0.8rem' }}>
                                                        Retrieve DOK reports first
                                                    </Typography>
                                                )}
                                            </TableCell>

                                            <TableCell sx={{ width: '140px', minWidth: '140px' }}>
                                                <FormControl fullWidth size="small">
                                                    <Select
                                                        value={row.overallAssessment}
                                                        onChange={(e) => handleOverallAssessmentChange(studentId, e.target.value)}
                                                        sx={{
                                                            fontSize: '0.8rem',
                                                            '& .MuiSelect-select': {
                                                                fontSize: '0.8rem',
                                                                padding: '8px 12px'
                                                            }
                                                        }}
                                                    >
                                                        <MenuItem value="Extending" sx={{ fontSize: '0.8rem' }}>Extending</MenuItem>
                                                        <MenuItem value="Proficient" sx={{ fontSize: '0.8rem' }}>Proficient</MenuItem>
                                                        <MenuItem value="Developing" sx={{ fontSize: '0.8rem' }}>Developing</MenuItem>
                                                        <MenuItem value="Emerging" sx={{ fontSize: '0.8rem' }}>Emerging</MenuItem>
                                                    </Select>
                                                </FormControl>
                                            </TableCell>


                                            <TableCell
                                                sx={{
                                                    width: { xs: 'auto', md: '55%' },
                                                    minWidth: '350px',
                                                    maxWidth: { xs: '500px', md: 'none' }
                                                }}
                                            >
                                                <Box sx={{ position: 'relative' }}>
                                                    <TextField
                                                        fullWidth
                                                        multiline
                                                        minRows={4}
                                                        maxRows={12}
                                                        size="small"
                                                        value={currentSummary}
                                                        onChange={(e) => handleSummaryEdit(studentId, e.target.value)}
                                                        placeholder={
                                                            selectedSubject && termNumber
                                                                ? "Summary will appear here after generation or load from DB…"
                                                                : "Select Subject and Term to load existing summaries…"
                                                        }
                                                        sx={{
                                                            '& .MuiInputBase-root': {
                                                                backgroundColor: generatedSummary ? '#f0f7ff' :
                                                                    existingSummary ? '#fff3e0' : 'white',
                                                                resize: 'vertical'
                                                            },
                                                            '& .MuiInputBase-input': {
                                                                lineHeight: '1.4',
                                                                fontSize: '0.875rem'
                                                            }
                                                        }}
                                                    />
                                                    {currentSummary && (
                                                        <IconButton
                                                            size="small"
                                                            sx={{ position: 'absolute', top: 4, right: 4 }}
                                                            onClick={() => handleCopyToClipboard(currentSummary)}
                                                        >
                                                            <ContentCopyIcon fontSize="small" />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap', mt: 0.5 }}>
                                                    {existingSummary && !generatedSummary && (
                                                        <Typography variant="caption" color="orange">
                                                            📄 Existing summary from database
                                                        </Typography>
                                                    )}
                                                    {generatedSummary && (
                                                        <Typography variant="caption" color="primary">
                                                            ✨ Newly generated summary
                                                        </Typography>
                                                    )}
                                                    {currentSummary && (
                                                        <Typography
                                                            variant="caption"
                                                            sx={{
                                                                fontWeight: 'bold',
                                                                color: 'text.secondary',
                                                                backgroundColor: '#f5f5f5',
                                                                px: 1,
                                                                py: 0.5,
                                                                borderRadius: 1
                                                            }}
                                                        >
                                                            {(currentSummary.match(/\b\w+\b/g) || []).length} words
                                                        </Typography>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        </TableRow>
                                    );
                                })}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
            )}

            {/* Results cards */}
            {Object.keys(batchResults).length > 0 && (
                <Box mt={4}>
                    <Typography
                        variant="h6"
                        fontWeight="bold"
                        mb={2}
                        sx={{
                            fontSize: { xs: '0.95rem', sm: '1rem' },
                            textAlign: { xs: 'center', sm: 'left' }
                        }}
                    >
                        Generated Comments ({processedCount} successful)
                    </Typography>

                    <Grid container spacing={{ xs: 2, md: 3 }}>
                        {Object.entries(batchResults).map(([studentId, result]) => (
                            <Grid item xs={12} md={6} xl={4} key={studentId}>
                                <Paper elevation={2} sx={{ p: 2 }}>
                                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                                        <Typography fontWeight="bold">
                                            {result.student_name}
                                        </Typography>
                                        <IconButton onClick={() => handleCopyToClipboard(result.summary)} size="small">
                                            <ContentCopyIcon fontSize="small" />
                                        </IconButton>
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            bgcolor: 'grey.50',
                                            p: 1,
                                            borderRadius: 1,
                                            minHeight: 100,
                                            fontSize: '0.9rem'
                                        }}
                                    >
                                        {result.summary}
                                    </Typography>

                                    <Typography
                                        variant="caption"
                                        sx={{
                                            fontWeight: 'bold',
                                            color: 'text.secondary',
                                            backgroundColor: '#f5f5f5',
                                            px: 1,
                                            py: 0.5,
                                            borderRadius: 1,
                                            display: 'inline-block',
                                            mt: 1
                                        }}
                                    >
                                        {(result.summary.match(/\b\w+\b/g) || []).length} words
                                    </Typography>

                                    {result.unit_reference_note && (
                                        <Typography variant="caption" color="textSecondary" mt={1} display="block">
                                            {result.unit_reference_note}
                                        </Typography>
                                    )}
                                </Paper>
                            </Grid>
                        ))}
                    </Grid>
                </Box>
            )}


            {/* Success Dialog */}
            <Dialog
                open={successDialog.open}
                onClose={() => setSuccessDialog({ open: false, message: "", title: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <CheckCircleIcon sx={{ fontSize: 48, color: 'success.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="success.main">
                            {successDialog.title}
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary">
                        {successDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setSuccessDialog({ open: false, message: "", title: "" })}
                        variant="contained"
                        color="success"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        Great!
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Dialog */}
            <Dialog
                open={errorDialog.open}
                onClose={() => setErrorDialog({ open: false, message: "" })}
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minWidth: 400
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                        <ErrorOutlineIcon sx={{ fontSize: 48, color: 'error.main' }} />
                        <Typography variant="h6" fontWeight="bold" color="error.main">
                            Attention Required
                        </Typography>
                    </Box>
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center', pt: 0 }}>
                    <Typography variant="body1" color="text.secondary" sx={{ whiteSpace: 'pre-line' }}>
                        {errorDialog.message}
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setErrorDialog({ open: false, message: "" })}
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            <BatchCommentLimitDialog
                open={showLimitDialog}
                onClose={() => setShowLimitDialog(false)}
                goToUpgrade={() => setActiveTabGlobal("Upgrade")}
            />

            {/* Units Modal */}
            <Dialog
                open={unitsModal.open}
                onClose={() => setUnitsModal({ open: false, studentName: "", units: [] })}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 3,
                        minHeight: '400px'
                    }
                }}
            >
                <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
                    <Typography variant="h6" fontWeight="bold" color="primary">
                        DOK Assessment Summary - {unitsModal.studentName}
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 2 }}>
                    <TableContainer component={Paper} elevation={1}>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>Unit Name</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', textAlign: 'center' }}>Overall Proficiency</TableCell>
                                    <TableCell sx={{ fontWeight: 'bold', fontSize: '0.9rem', textAlign: 'center' }}>Status</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {unitsModal.units.map((unit, index) => (
                                    <TableRow key={index} sx={{ '&:hover': { backgroundColor: '#f5f5f5' } }}>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight="medium">
                                                {unit.unit_name}
                                            </Typography>
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {unit.dok_exists ? (
                                                <Chip
                                                    label={unit.overall_unit_assessment || "Not Assessed"}
                                                    size="small"
                                                    color={
                                                        unit.overall_unit_assessment === "Extending" ? "success" :
                                                            unit.overall_unit_assessment === "Proficient" ? "primary" :
                                                                unit.overall_unit_assessment === "Developing" ? "warning" :
                                                                    unit.overall_unit_assessment === "Emerging" ? "error" : "default"
                                                    }
                                                    variant="outlined"
                                                />
                                            ) : (
                                                <Chip
                                                    label="Not Assessed"
                                                    size="small"
                                                    color="default"
                                                    variant="outlined"
                                                />
                                            )}
                                        </TableCell>
                                        <TableCell sx={{ textAlign: 'center' }}>
                                            {unit.dok_exists ? (
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                    <CheckCircleIcon color="success" fontSize="small" />
                                                    <Typography variant="caption" color="success.main" fontWeight="medium">
                                                        Assessed
                                                    </Typography>
                                                </Box>
                                            ) : (
                                                <Box display="flex" alignItems="center" justifyContent="center" gap={1}>
                                                    <CancelIcon color="error" fontSize="small" />
                                                    <Typography variant="caption" color="error.main" fontWeight="medium">
                                                        Not Assessed
                                                    </Typography>
                                                </Box>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </DialogContent>
                <DialogActions sx={{ justifyContent: 'center', pb: 3 }}>
                    <Button
                        onClick={() => setUnitsModal({ open: false, studentName: "", units: [] })}
                        variant="contained"
                        color="primary"
                        sx={{ minWidth: 100, borderRadius: 2 }}
                    >
                        Close
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default BatchSmartSubjectComment;
